import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ErrorAlert } from '../components/ErrorAlert.tsx';
import { TextField } from '../components/TextField.tsx';
import { useAuth } from '../features/auth/useAuth.ts';
import { hasErrors, validateLogin, type FieldErrors, type LoginField } from '../lib/validation.ts';

export function LoginPage() {
  const { t } = useTranslation();
  const { login, sessionExpired, clearSessionExpired } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors<LoginField>>({});
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitError(null);
    clearSessionExpired();

    const errors = validateLogin({ email, password });
    setFieldErrors(errors);
    if (hasErrors(errors)) return;

    setPending(true);
    try {
      await login(email.trim(), password);
      // Vuelve a donde quería ir antes de que se le pidiera identificarse.
      const from = (location.state as { from?: string } | null)?.from;
      void navigate(from && from !== '/login' ? from : '/', { replace: true });
    } catch (error) {
      // El backend responde lo MISMO para email inexistente y contraseña incorrecta,
      // a propósito. Aquí no se intenta adivinar cuál de los dos es: hacerlo
      // convertiría el formulario en un comprobador de qué emails están registrados.
      setSubmitError(error);
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <h2 className="mb-4 text-lg font-bold">{t('auth.login.title')}</h2>

      {sessionExpired ? (
        <p className="aviso-info mb-4" role="status">
          {t('auth.sessionExpired')}
        </p>
      ) : null}

      <ErrorAlert error={submitError} />

      <form onSubmit={handleSubmit} noValidate data-test="formulario-login">
        <TextField
          label={t('auth.fields.email')}
          type="email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          inputMode="email"
          autoCapitalize="none"
          spellCheck={false}
          required
          error={fieldErrors.email ? t(fieldErrors.email) : undefined}
        />
        <TextField
          label={t('auth.fields.password')}
          type="password"
          name="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
          error={fieldErrors.password ? t(fieldErrors.password) : undefined}
        />
        <button className="btn-primario" type="submit" disabled={pending}>
          {pending ? t('auth.login.pending') : t('auth.login.submit')}
        </button>
      </form>

      <p className="mt-4 text-center text-sm text-tenue">
        {t('auth.login.noAccount')} <Link to="/register" className="text-acento">{t('auth.login.goToRegister')}</Link>
      </p>
    </>
  );
}
